import { useEffect, useMemo, useState } from 'react';
import { LoadingFallback } from './LoadingFallback';
import {
  revokeEnterpriseAccessToken,
  validateEnterpriseAccessToken,
  verifyEnterpriseAccessPassword,
} from '../lib/enterprise-api';

const ENTERPRISE_ACCESS_TOKEN_KEY = 'tension_enterprise_access_token';
const ENTERPRISE_ACCESS_EXPIRY_KEY = 'tension_enterprise_access_expires_at';

type Props = {
  children: React.ReactNode;
};

function readEnterpriseToken(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return sessionStorage.getItem(ENTERPRISE_ACCESS_TOKEN_KEY);
  } catch {
    return null;
  }
}

function clearEnterpriseToken(): void {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.removeItem(ENTERPRISE_ACCESS_TOKEN_KEY);
    sessionStorage.removeItem(ENTERPRISE_ACCESS_EXPIRY_KEY);
  } catch {
    // ignore storage failures
  }
}

function persistEnterpriseToken(token: string, expiresAt?: string): void {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.setItem(ENTERPRISE_ACCESS_TOKEN_KEY, token);
    if (expiresAt) sessionStorage.setItem(ENTERPRISE_ACCESS_EXPIRY_KEY, expiresAt);
  } catch {
    // ignore storage failures
  }
}

export function EnterpriseAccessRoute({ children }: Props) {
  const [checking, setChecking] = useState(true);
  const [granted, setGranted] = useState(false);
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);

  const expiresLabel = useMemo(() => {
    if (!expiresAt) return null;
    const parsed = new Date(expiresAt);
    if (Number.isNaN(parsed.getTime())) return null;
    return parsed.toLocaleString();
  }, [expiresAt]);

  useEffect(() => {
    let cancelled = false;
    const token = readEnterpriseToken();
    if (!token) {
      setChecking(false);
      setGranted(false);
      return () => {
        cancelled = true;
      };
    }
    validateEnterpriseAccessToken(token).then((result) => {
      if (cancelled) return;
      if (result.success && result.valid) {
        setGranted(true);
        setExpiresAt(result.expiresAt || null);
      } else {
        clearEnterpriseToken();
        setGranted(false);
      }
      setChecking(false);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  if (checking) {
    return <LoadingFallback fullScreen message="Verifying enterprise access..." />;
  }

  if (!granted) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-md rounded-2xl border border-neutral-200 bg-white/90 p-6 shadow-xl backdrop-blur dark:border-neutral-700 dark:bg-neutral-900/90">
          <p className="text-xs font-semibold uppercase tracking-wide enterprise-accent-text">Enterprise access</p>
          <h1 className="mt-2 text-xl font-semibold text-neutral-900 dark:text-neutral-100">Protected workspace</h1>
          <p className="mt-2 text-sm text-neutral-600 dark:text-neutral-400">
            Enter the enterprise access password to continue.
          </p>
          <form
            className="mt-5 space-y-3"
            onSubmit={async (event) => {
              event.preventDefault();
              if (!password.trim() || submitting) return;
              setSubmitting(true);
              setError(null);
              const result = await verifyEnterpriseAccessPassword(password);
              setSubmitting(false);
              if (!result.success || !result.token) {
                setError(result.error || 'Access denied');
                return;
              }
              persistEnterpriseToken(result.token, result.expiresAt);
              setExpiresAt(result.expiresAt || null);
              setPassword('');
              setGranted(true);
            }}
          >
            <input
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete="current-password"
              className="w-full rounded-xl border border-neutral-300 bg-white px-3 py-2 text-sm text-neutral-900 outline-none ring-0 focus:border-violet-500 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-100"
              placeholder="Enterprise password"
              required
            />
            {error ? <p className="text-sm text-red-600 dark:text-red-400">{error}</p> : null}
            <button
              type="submit"
              disabled={submitting}
              className="w-full rounded-xl bg-neutral-900 px-4 py-2 text-sm font-semibold text-white hover:bg-black disabled:cursor-not-allowed disabled:opacity-60 dark:bg-neutral-100 dark:text-neutral-900 dark:hover:bg-white"
            >
              {submitting ? 'Verifying...' : 'Unlock enterprise'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <>
      {children}
      <div className="sr-only" aria-live="polite">
        {expiresLabel ? `Enterprise access valid until ${expiresLabel}` : 'Enterprise access granted'}
      </div>
    </>
  );
}

export async function clearEnterpriseAccessSession(): Promise<void> {
  const token = readEnterpriseToken();
  clearEnterpriseToken();
  if (token) await revokeEnterpriseAccessToken(token);
}
